import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MODULE_KEY_BY_PATH_PREFIX, isModuleEnabled } from '../constants/modules';
import { canViewPath } from '../constants/permissions';
import { ShieldX } from 'lucide-react';

const AccessDeniedView = () => (
  <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
    <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md mx-auto my-12 border border-slate-200">
      <div className="mx-auto w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4 text-rose-600">
        <ShieldX className="w-8 h-8" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">403 Access Denied</h2>
      <p className="text-sm text-slate-600 mb-4">
        You do not have permission to access this page.
      </p>
      <p className="text-xs text-slate-400">
        If you believe this is an error, please contact your administrator to request access.
      </p>
    </div>
  </div>
);

/**
 * Wraps children and renders 403 Access Denied inside the layout if either:
 *   1. The current route's tenant module is disabled (Module Control), or
 *   2. The user lacks the `view` permission for the route (Permission Matrix).
 *
 * Super Admin bypasses both guards.
 */
const ModuleGate = ({ children, renderAccessDenied = true }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (user?.role === 'super_admin') return children;

  // 1. Tenant module gate (existing behaviour)
  const match = MODULE_KEY_BY_PATH_PREFIX.find((m) =>
    location.pathname === m.prefix || location.pathname.startsWith(`${m.prefix}/`)
  );
  if (match && !isModuleEnabled(user, match.key)) {
    return renderAccessDenied ? <AccessDeniedView /> : <Navigate to="/dashboard" replace />;
  }

  // 2. Permission matrix gate. Only enforce when user.permissions is present
  // (older sessions without it skip the check, preserving back-compat).
  if (user && Array.isArray(user.permissions) && user.permissions.length > 0) {
    if (!canViewPath(user, location.pathname)) {
      return renderAccessDenied ? <AccessDeniedView /> : <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default ModuleGate;

